<script setup lang="ts">
import { computed, onMounted } from "vue";

import {
  THRESHOLD_EXPIRY_DANGER,
  THRESHOLD_EXPIRY_WARNING,
  THRESHOLD_RECOVERY_DANGER,
  THRESHOLD_RECOVERY_WARNING,
} from "@/stores/retention-store";
import { useBrandStore, useRetentionStore, useUserPermissionStore } from "@/stores";

import Loading from "@/components/Loading.vue";

import Tooltip from "@/components/Tooltip/Tooltip.vue";

import RetentionSection from "./RetentionSection.vue";
import RetentionRecoverCase from "./RetentionRecoverCase.vue";
import RetentionDurationEdit from "./RetentionDurationEdit.vue";
import RetentionQuickExtensions from "./RetentionQuickExtensions.vue";
import RetentionApplyLegalHold from "./RetentionApplyLegalHold.vue";
import RetentionCancelDeletion from "./RetentionCancelDeletion.vue";
import RetentionCancelLegalHold from "./RetentionCancelLegalHold.vue";
import RetentionRequestDeletion from "./RetentionRequestDeletion.vue";
import RetentionApproveDeletion from "./RetentionApproveDeletion.vue";
import RetentionOverrideStartDate from "./RetentionOverrideStartDate.vue";

import {
  $can,
  addDuration,
  formatDuration,
  parseDuration,
  dateInputToUTCDateTime,
  fromUtc,
} from "@/services/UtilitiesService";

import type { CaseRetentionHistory } from "@/services/RetentionService";

const brandStore = useBrandStore();
const retentionStore = useRetentionStore();
const userPermissionStore = useUserPermissionStore();

const authorizedUsersMayDeleteBeforeExpiry = computed(
  () => brandStore.brandConfig.retention.authorizedUsersMayDeleteBeforeExpiry,
);

const sanitizeUserId = (value: string | null) => value?.slice(value.lastIndexOf("|") + 1) ?? null;

const componentKey = computed(() => {
  if (retentionStore.state.status !== "ready") return;

  const { duration, status } = retentionStore.state.data;
  return `${status.status}${duration.effectiveExpiryDate ?? ""}${duration.caseStartDateOverride ?? duration.caseStartDate ?? ""}`;
});

const retentionState = computed(() => {
  if (retentionStore.state.status !== "ready") return;

  const { duration, status, history } = retentionStore.state.data;

  return {
    status,
    history,
    caseStartDate:
      duration?.caseStartDate == null
        ? undefined
        : {
            original: dateInputToUTCDateTime(duration.caseStartDate),
            override:
              duration.caseStartDateOverride == null
                ? undefined
                : dateInputToUTCDateTime(duration.caseStartDateOverride),
          },
    duration: (() => {
      if (duration.computedFrom === "none") return undefined;

      return {
        computedFrom: duration.computedFrom,
        isIndefinite: duration.isIndefinite,
        duration: duration.caseRetentionDuration,
        expiry: (() => {
          if (duration.isIndefinite) return;

          const expiryDate = dateInputToUTCDateTime(duration.effectiveExpiryDate, true);

          const daysToExpiry = Math.max(
            0,
            Math.ceil((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
          );

          return { daysToExpiry, expiryDate };
        })(),
        recoveryPeriod: (() => {
          if (status.status !== "RecoveryPeriod") return;

          const recoveryDuration = brandStore.brandConfig.retention.deletedCaseRecoveryDuration;
          if (recoveryDuration == null) return 0;

          return Math.max(
            0,
            Math.ceil(
              (addDuration(status.createdAt, recoveryDuration).getTime() - new Date().getTime()) /
                (1000 * 60 * 60 * 24),
            ),
          );
        })(),
      };
    })(),
  };
});

const retentionHistoryStart = computed(() => {
  const rows = retentionState.value?.history.data;
  if (rows == null) return 0;
  return (rows.page - 1) * rows.pageSize + 1;
});

function getHistoryValueDisplay(field: CaseRetentionHistory["field"], value: string) {
  function formatHistoryDate(value: string | null) {
    if (value == null) return "Cleared";

    if (value === "Indefinite") return value;

    const date = new Date(value);
    if (isNaN(date.getTime())) return "Unknown";

    return fromUtc(date, { dateStyle: "medium" });
  }

  if (field === "CaseStartDateOverride") return formatHistoryDate(value);

  if (field === "RetentionDuration") {
    const duration = parseDuration(value);
    return Object.keys(duration).length > 0 ? formatDuration(duration) : formatHistoryDate(value);
  }

  return value;
}

onMounted(() => retentionStore.load());
</script>
<template>
  <div
    v-if="retentionState != null"
    :key="componentKey"
    class="tw-grid tw-grid-cols-1 tw-content-start tw-gap-3 tw-p-3 tw-text-gray-900 @xl:tw-grid-cols-3 @3xl:tw-grid-cols-3 @6xl:tw-grid-cols-5"
  >
    <div
      v-if="retentionStore.isActionPending"
      role="status"
      aria-live="polite"
      class="tw-col-span-full tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-200 tw-p-1.5 tw-text-sm tw-text-gray-900"
    >
      <i class="fas fa-circle-info tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
      <p class="tw-flex-1">
        A retention change has been requested. Please wait for it to be applied before making further changes, as they
        may be rejected.
      </p>
      <button
        type="button"
        class="tw-w-7 tw-shrink-0 tw-rounded tw-p-1 tw-ring-2 tw-ring-transparent hover:tw-ring-gray-500 focus:tw-outline-none focus:tw-ring-gray-500"
        aria-label="Dismiss notification"
        @click="retentionStore.clearPendingAction"
      >
        <i class="fas fa-xmark" aria-hidden="true" />
      </button>
    </div>
    <RetentionSection title="Duration" class="tw-col-span-2">
      <template v-if="retentionState.duration != null">
        <dl>
          <dt>Start Date</dt>
          <dd>
            <template v-if="retentionState.caseStartDate == null">Not Set</template>
            <template v-else>
              <template v-if="retentionState.caseStartDate.override != null">
                {{ fromUtc(retentionState.caseStartDate.override, { dateStyle: "medium" }) }}
                <Tooltip>
                  <template #trigger>
                    <button
                      type="button"
                      class="tw-ml-1 tw-cursor-default tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-font-[inherit] tw-text-xs tw-text-gray-600"
                    >
                      (Overridden)
                    </button>
                  </template>
                  <template #content>
                    {{
                      `Original: ${fromUtc(retentionState.caseStartDate.original, { dateStyle: "medium", timeStyle: "short" })}`
                    }}
                  </template>
                </Tooltip>
              </template>
              <template v-else>
                {{ fromUtc(retentionState.caseStartDate.original, { dateStyle: "medium" }) }}
              </template>
            </template>
          </dd>
          <dt>Retention Duration</dt>
          <dd>
            {{
              retentionState.duration.duration == null
                ? "Indefinite"
                : formatDuration(parseDuration(retentionState.duration.duration))
            }}
          </dd>
          <dt>Effective Expiry</dt>
          <dd>
            <span v-if="retentionState.status.status === 'RecoveryPeriod'">---</span>
            <span v-else-if="retentionState.duration.expiry == null" class="tw-font-medium tw-text-gray-600">
              Indefinite
            </span>
            <span v-else>
              {{ fromUtc(retentionState.duration.expiry.expiryDate, { dateStyle: "medium" }) }}
              <span
                class="tw-ml-1"
                :class="
                  retentionState.duration.expiry.daysToExpiry <= THRESHOLD_EXPIRY_DANGER
                    ? 'tw-text-red-600'
                    : retentionState.duration.expiry.daysToExpiry <= THRESHOLD_EXPIRY_WARNING
                      ? 'tw-text-orange-600'
                      : undefined
                "
              >
                (In {{ retentionState.duration.expiry.daysToExpiry }}
                {{ retentionState.duration.expiry.daysToExpiry === 1 ? "day" : "days" }})
              </span>
            </span>
          </dd>
          <dt>Computed From</dt>
          <dd>
            <span
              v-if="retentionState.duration.computedFrom === 'brand-default'"
              class="tw-font-medium tw-text-gray-600"
            >
              Default
            </span>
            <span v-else>Case Override</span>
          </dd>
        </dl>
        <RetentionQuickExtensions v-if="!retentionState.duration.isIndefinite && $can('changeCaseRetentionDuration')" />
      </template>
      <RetentionDurationEdit
        v-if="
          (retentionState.status.status === 'Retained' || retentionState.status.status === 'Expired') &&
          $can('changeCaseRetentionDuration') &&
          retentionState.caseStartDate != null
        "
        :current-duration-i-s-o="retentionState.duration?.duration"
        :current-expiry="retentionState.duration?.expiry?.expiryDate"
        :duration-state="
          retentionState.duration == null
            ? 'UNSET'
            : retentionState.duration.isIndefinite
              ? 'CASE-INDEFINITE'
              : retentionState.duration.computedFrom === 'case-level'
                ? 'CASE'
                : 'BRAND'
        "
        :start-date="retentionState.caseStartDate.override ?? retentionState.caseStartDate.original"
      />
    </RetentionSection>
    <RetentionSection title="Actions">
      <div class="tw-peer tw-flex tw-flex-wrap tw-gap-2 tw-overflow-x-auto *:tw-shrink-0 empty:tw-hidden">
        <template v-if="$can('changeLegalHoldStatus')">
          <RetentionCancelLegalHold v-if="retentionState.status?.status === 'LegalHold'" />
          <RetentionApplyLegalHold v-else />
        </template>
        <RetentionRequestDeletion
          v-if="
            (retentionState.status?.status === 'Expired' ||
              (retentionState.status?.status === 'Retained' && authorizedUsersMayDeleteBeforeExpiry)) &&
            $can('requestCaseDeletion')
          "
        />
        <RetentionCancelDeletion
          v-else-if="
            retentionState.status?.status === 'DeletionRequested' &&
            ($can('actionCaseDeletion') ||
              (sanitizeUserId(retentionState.status.createdBy) === userPermissionStore.userName &&
                $can('requestCaseDeletion')))
          "
        />
        <RetentionApproveDeletion
          v-if="
            retentionState.status?.status === 'DeletionRequested' &&
            sanitizeUserId(retentionState.status.createdBy) !== userPermissionStore.userName &&
            $can('actionCaseDeletion')
          "
          :requester-name="sanitizeUserId(retentionState.status.createdBy)"
          :deletion-reason="retentionState.status.reason"
        />
        <RetentionRecoverCase v-if="retentionState.status?.status === 'RecoveryPeriod' && $can('undeleteCases')" />
        <RetentionOverrideStartDate
          v-if="$can('changeCaseRetentionDuration') && retentionState.caseStartDate != null"
          :original-date="retentionState.caseStartDate.original"
          :override-date="retentionState.caseStartDate.override"
        />
      </div>
      <p class="tw-hidden tw-text-sm peer-empty:tw-block">No actions available.</p>
    </RetentionSection>
    <RetentionSection title="Status & History" class="tw-col-span-full">
      <dl>
        <dt>Current Status</dt>
        <dd>
          <template v-if="retentionState.status.status === 'Retained'">Retained</template>
          <template v-else-if="retentionState.status.status === 'Expired'">Expired</template>
          <template v-else-if="retentionState.status.status === 'DeletionInProgress'">Deletion In Progress</template>
          <template v-else-if="retentionState.status.status === 'DeletionRequired'">Deletion Required</template>
          <template v-else-if="retentionState.status.status === 'LegalHold'">Legal Hold</template>
          <template v-else-if="retentionState.status.status === 'DeletionRequested'">
            Deletion Requested. Awaiting Confirmation
          </template>
          <template v-else-if="retentionState.status.status === 'RecoveryPeriod'">
            Pending Recovery
            <span
              v-if="retentionState.duration?.recoveryPeriod != null"
              class="tw-ml-1"
              :class="
                retentionState.duration.recoveryPeriod == null ||
                retentionState.duration.recoveryPeriod > THRESHOLD_RECOVERY_WARNING
                  ? undefined
                  : retentionState.duration.recoveryPeriod <= THRESHOLD_RECOVERY_DANGER
                    ? 'tw-text-red-600'
                    : 'tw-text-orange-600'
              "
              >({{ retentionState.duration.recoveryPeriod }}
              {{ retentionState.duration.recoveryPeriod === 1 ? "day" : "days" }} left to recover)
            </span>
          </template>
          <template v-else>Unknown Status. Please contact your support team.</template>
        </dd>
        <template v-if="retentionState.status.reason != null">
          <dt>Reason</dt>
          <dd>{{ retentionState.status.reason ?? "Not Provided" }}</dd>
        </template>
        <template v-if="retentionState.status.createdBy != null">
          <dt>Actioned By</dt>
          <dd>{{ sanitizeUserId(retentionState.status.createdBy) }}</dd>
        </template>
      </dl>
      <template v-if="retentionState.history.data != null">
        <template v-if="retentionState.history.data.totalCount > 0">
          <div class="tw-flex tw-items-center tw-justify-start tw-gap-2">
            <p>
              {{
                `Showing ${retentionHistoryStart}-${retentionHistoryStart + retentionState.history.data.items.length - 1} of ${retentionState.history.data.totalCount}`
              }}
            </p>
            <div class="tw-flex-auto" />
            <span v-if="retentionState.history.isLoading">
              <i aria-hidden="true" class="fas fa-circle-notch animate-spin" />
            </span>
            <button
              type="button"
              class="tw-rounded tw-border tw-px-2 tw-py-1 hover:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              :disabled="retentionState.history.data.page <= 1 || retentionState.history.isLoading"
              :aria-busy="retentionState.history.isLoading"
              @click="retentionStore.loadHistory(retentionState.history.data.page - 1)"
            >
              Previous
            </button>
            <button
              type="button"
              class="tw-rounded tw-border tw-px-2 tw-py-1 hover:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              :disabled="
                retentionState.history.data.page >=
                  Math.ceil(retentionState.history.data.totalCount / retentionState.history.data.pageSize) ||
                retentionState.history.isLoading
              "
              :aria-busy="retentionState.history.isLoading"
              @click="retentionStore.loadHistory(retentionState.history.data.page + 1)"
            >
              Next
            </button>
          </div>
          <div class="tw-w-full">
            <table
              class="tw-text-sm [&_*:is(th,td):not(:last-child)]:tw-pr-4 [&_*:is(th,td)]:tw-px-4 [&_*:is(th,td)]:tw-py-1.5"
            >
              <caption class="tw-sr-only">
                Retention history
              </caption>
              <thead>
                <tr class="tw-border-b tw-text-left tw-text-xs *:tw-font-medium">
                  <th scope="col">#</th>
                  <th scope="col">Date</th>
                  <th scope="col">What Changed</th>
                  <th scope="col">Value</th>
                  <th scope="col">Actioned By</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody class="tw-divide-y">
                <tr v-for="(entry, i) in retentionState.history.data.items" :key="i">
                  <td class="tw-w-px">
                    {{ `${retentionHistoryStart + i}.` }}
                  </td>
                  <td class="tw-text-xs tw-text-gray-600">
                    {{ fromUtc(entry.createdAt, { dateStyle: "medium", timeStyle: "short" }) }}
                  </td>
                  <td>
                    {{
                      entry.field === "RetentionDuration"
                        ? "Retention Duration"
                        : entry.field === "CaseStartDateOverride"
                          ? "Start Date"
                          : entry.field
                    }}
                  </td>
                  <td>{{ getHistoryValueDisplay(entry.field, entry.value) }}</td>
                  <td class="tw-max-w-md tw-break-words">
                    {{ sanitizeUserId(entry.createdBy) ?? "-" }}
                  </td>
                  <td class="tw-max-w-md tw-break-words tw-py-1.5 tw-text-gray-600">
                    {{ entry.reason ?? "—" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <p v-else class="tw-text-sm">No retention history available.</p>
      </template>
      <div v-else-if="retentionState.history.isLoading">
        <Loading id="retention-history" message="Loading Retention History" :absolute="false" />
      </div>
      <div v-else role="alert" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
        <p>Error loading retention history.</p>
        <button
          type="button"
          class="tw-ml-2 tw-rounded tw-border tw-px-2 tw-py-1 hover:tw-bg-gray-100"
          @click="retentionStore.loadHistory(retentionState.history.requestedPage)"
        >
          Retry
        </button>
      </div>
    </RetentionSection>
  </div>
  <Loading v-else-if="retentionStore.state.status === 'loading'" id="retention" message="Loading Retention Details" />
  <div v-else role="alert" class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
    Error loading retention details.
  </div>
</template>
<style scoped>
dl {
  @apply tw-grid tw-grid-cols-[max-content_1fr] tw-items-baseline tw-gap-x-6;
}

dt {
  @apply tw-py-1.5 tw-text-xs tw-text-gray-800;
}

dd {
  @apply tw-py-1.5 tw-text-sm;
}
</style>
